import { Seo } from '@frontend/shared/web';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ASSIGNABLE_ROLES } from '../../components/admin/roles';
import { UserRolesModal } from '../../components/admin/UserRolesModal';
import { UsersTable } from '../../components/admin/UsersTable';
import { Header } from '../../components/layout/Header';
import { useAccount } from '../../contexts/AccountContext';
import { useUsersList } from '../../services/api/hooks/useUsersList';
import type { UserV2Output } from '../../types/extranet-api.types';

export const AdminRoute = () => {
  const { me, can } = useAccount();
  const canManage = can('admin', 'manageUsers');
  const [searchParams, setSearchParams] = useSearchParams();
  const [editingUser, setEditingUser] = useState<UserV2Output | null>(null);

  const search = searchParams.get('search') ?? '';
  const roleFilter = searchParams.getAll('role');

  const query = useUsersList({
    accountKey: me.accountId,
    roles: roleFilter.length > 0 ? roleFilter : undefined,
  });

  const filteredItems = useMemo(() => {
    const items = query.data?.items ?? [];
    if (!search.trim()) return items;
    const s = search.trim().toLowerCase();
    return items.filter(
      u =>
        (u.email?.toLowerCase().includes(s) ?? false) ||
        `${u.firstName ?? ''} ${u.lastName ?? ''}`.toLowerCase().includes(s),
    );
  }, [query.data?.items, search]);

  const updateParams = (mutate: (p: URLSearchParams) => void) =>
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      mutate(next);
      return next;
    });

  return (
    <>
      <Seo title="Administration — Portail extranet" />
      <Header title="Administration" subtitle="Utilisateurs &amp; rôles" />

      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-6 flex flex-col gap-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                value={search}
                onChange={e =>
                  updateParams(p => {
                    if (e.target.value) p.set('search', e.target.value);
                    else p.delete('search');
                  })
                }
                placeholder="Rechercher par nom ou email"
                className="h-8 px-3 text-sm bg-white border border-zinc-300 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors w-72"
              />
              <div className="flex items-center gap-1.5">
                {ASSIGNABLE_ROLES.map(role => {
                  const active = roleFilter.includes(role);
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() =>
                        updateParams(p => {
                          const current = p.getAll('role');
                          p.delete('role');
                          const next = active ? current.filter(x => x !== role) : [...current, role];
                          next.forEach(v => p.append('role', v));
                        })
                      }
                      className={[
                        'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-xs font-medium font-mono tabular-nums transition-colors duration-100',
                        active
                          ? 'bg-zinc-900 text-white border border-zinc-900'
                          : 'bg-white text-zinc-700 border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50',
                      ].join(' ')}
                    >
                      {role}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <UsersTable
            items={filteredItems}
            total={query.data?.items?.length ?? 0}
            isLoading={query.isLoading}
            isFetching={query.isFetching}
            canManage={canManage}
            onEditRoles={user => setEditingUser(user)}
          />
        </div>
      </div>

      <UserRolesModal user={editingUser} onClose={() => setEditingUser(null)} />
    </>
  );
};
